import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselApi } from '@/components/ui/carousel';
import { ChevronRight, Clock, Play, Briefcase, TrendingUp, Rocket, Loader2, Pause } from 'lucide-react';
import staticTemplates from '@/data/newsletterTemplates.json';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { trackInteractivePreview, trackConversionFunnel } from '@/components/GoogleAnalytics';
import { trackInteractivePreviewHj, trackConversionFunnelHj } from '@/components/Hotjar';
import { trackInteractivePreviewMp, trackConversionFunnelMp } from '@/components/Mixpanel';
import { sentryUtils } from '@/utils/sentry';
import ErrorBoundary from './ErrorBoundary';

interface NewsletterTemplate {
  id: string;
  title: string;
  description: string;
  interests: string;
  html_content: string;
  html_content_en?: string;
  gradient: string;
  color: string;
  title_en?: string;
  description_en?: string;
  interests_en?: string;
}

interface InteractiveNewsletterPreviewProps {
  onSelectTemplate?: (interests: string) => void;
  variant?: 'carousel' | 'rotating';
  showCycleButton?: boolean;
  className?: string;
}

export const InteractiveNewsletterPreview = ({ 
  onSelectTemplate, 
  variant = 'carousel',
  showCycleButton = false,
  className = ''
}: InteractiveNewsletterPreviewProps) => {
  const { t, i18n } = useTranslation();
  // Static snapshot of the preview templates (avoids edge function calls / egress)
  const [templates] = useState<NewsletterTemplate[]>(() => {
    const list = staticTemplates as NewsletterTemplate[];
    const start = Math.floor(Math.random() * list.length);
    return [...list.slice(start), ...list.slice(0, start)];
  });
  const isLoadingFirst = false;
  const error: string | null = null;
  const [activeTemplate, setActiveTemplate] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isPermanentlyPaused, setIsPermanentlyPaused] = useState(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentTime, setCurrentTime] = useState('');
  const [isInteracting, setIsInteracting] = useState(false);
  const { toast } = useToast();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const interactionTimeoutRef = useRef<NodeJS.Timeout>();

  // Get dynamic theme color from template
  const getThemeColor = () => {
    return templates[activeTemplate]?.color || '#3b82f6'; // fallback to blue
  };

  const getGlowStyles = () => {
    const color = getThemeColor();
    return {
      boxShadow: `0 25px 50px -12px ${color}40, 0 0 0 1px ${color}20`,
      backgroundColor: `${color}10`
    };
  };

  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
      const timeString = now.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: locale === 'en-US'
      });
      setCurrentTime(timeString);
    };

    // Update immediately
    updateTime();

    // Update every minute
    const interval = setInterval(updateTime, 60000);
    
    return () => clearInterval(interval);
  }, []);


  // Auto-rotation effect with interaction detection
  useEffect(() => {
    if (!isAutoRotating || isPaused || isPermanentlyPaused || templates.length === 0) return;
    
    const interval = setInterval(() => {
      setActiveTemplate(prev => {
        const newIndex = (prev + 1) % templates.length;
        // Sync carousel with auto-rotation
        if (carouselApi) {
          carouselApi.scrollTo(newIndex);
        }
        return newIndex;
      });
    }, 12000); // Increased from 6s to 12s

    return () => clearInterval(interval);
  }, [isAutoRotating, isPaused, isPermanentlyPaused, templates.length, carouselApi]);

  // Carousel synchronization effect
  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      const selectedIndex = carouselApi.selectedScrollSnap();
      setActiveTemplate(selectedIndex);
      // Pause auto-rotation when user interacts with carousel
      pauseAutoRotation();
    };

    carouselApi.on("select", onSelect);
    
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  // Sync carousel position when activeTemplate changes (from other sources like dots)
  useEffect(() => {
    if (carouselApi && templates.length > 0) {
      carouselApi.scrollTo(activeTemplate);
    }
  }, [activeTemplate, carouselApi, templates.length]);

  // Pause auto-rotation on interaction
  const pauseAutoRotation = (duration: number = 15000) => {
    if (isPermanentlyPaused) return; // Don't override permanent pause
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), duration);
  };

  // Toggle permanent pause
  const togglePermanentPause = () => {
    setIsPermanentlyPaused(prev => !prev);
    // Clear any temporary pause when toggling permanent pause
    setIsPaused(false);
  };

  const handleTemplateSelect = (index: number) => {
    setActiveTemplate(index);
    setIsAutoRotating(false);
    
    // Track template selection
    const template = templates[index];
    if (template) {
      sentryUtils.trackTemplateInteraction('select', template.id);
      trackInteractivePreview('template_selected', template.id);
      trackInteractivePreviewHj('template_selected', template.id);
      trackInteractivePreviewMp('template_selected', template.id);
    }
    
    // Re-enable auto-rotation after 20 seconds
    setTimeout(() => setIsAutoRotating(true), 20000);
  };

  const handleGetStarted = (template: NewsletterTemplate) => {
    // Track conversion funnel step
    trackConversionFunnel('preview_cta_clicked', 1);
    trackConversionFunnelHj('preview_cta_clicked', 1);
    trackConversionFunnelMp('preview_cta_clicked', 1);
    sentryUtils.trackConversionFunnel('preview_cta_clicked', 1, { templateId: template.id });
    
    onSelectTemplate?.(template.interests);
    
    // Scroll to newsletter form
    const formElement = document.getElementById('newsletter-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const currentTemplate = templates[activeTemplate];


  // Build clean HTML document for iframe (no scripts)
  const buildSrcDoc = (html: string) => {
    // Enhanced HTML sanitization for security
    const cleanHtml = html
      // Remove all script tags (including multi-line)
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gis, '')
      // Remove all event handler attributes (onclick, onload, etc.)
      .replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '')
      // Remove javascript: URLs
      .replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"')
      // Force all links to use # to prevent navigation
      .replace(/(<a[^>]*\s+)href\s*=\s*["'][^"']*["']/gi, '$1href="#"');
    
    return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta http-equiv="Content-Security-Policy" content="script-src 'none'; object-src 'none';">
    <title>Newsletter Preview</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            overflow-x: auto;
            background: #f8f9fa;
        }
        * {
            box-sizing: border-box;
        }
        a, a:hover { 
            cursor: pointer !important; 
            text-decoration: underline;
            -webkit-touch-callout: default !important;
            touch-action: manipulation !important;
        }
    </style>
</head>
<body>
    ${cleanHtml}
</body>
</html>`;
  };

  // Click handlers for iframe content
  const onDocClick = useCallback((event: Event) => {
    const target = (event.target as Element)?.closest('a, button, [role="button"], [onclick]');
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      
      // Show toast for link/button interaction
      toast({
        title: t('interactivePreview.toast.title'),
        description: t('interactivePreview.toast.description'),
        action: (
          <Button
            size="sm"
            onClick={() => handleGetStarted(currentTemplate)}
            className="ml-2 bg-primary hover:bg-primary/90"
          >
            {t('interactivePreview.toast.button')}
          </Button>
        ),
        duration: 8000,
      });
    }
  }, [toast, t, currentTemplate, handleGetStarted]);

  const onDocTouchEnd = useCallback((event: Event) => {
    const target = (event.target as Element)?.closest('a, button, [role="button"], [onclick]');
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      
      // Show toast for touch interaction
      toast({
        title: t('interactivePreview.toast.title'),
        description: t('interactivePreview.toast.description'),
        action: (
          <Button
            size="sm"
            onClick={() => handleGetStarted(currentTemplate)}
            className="ml-2 bg-primary hover:bg-primary/90"
          >
            {t('interactivePreview.toast.button')}
          </Button>
        ),
        duration: 8000,
      });
    }
  }, [toast, t, currentTemplate, handleGetStarted]);

  // Handle iframe scroll/touch events
  const handleIframeInteraction = useCallback(() => {
    // Send message to parent to pause rotation
    window.postMessage({ type: 'iframe-interaction' }, window.location.origin);
  }, []);

  // Attach event listeners to iframe content
  const attachIframeListeners = useCallback(() => {
    if (!iframeRef.current?.contentDocument) return;
    
    const doc = iframeRef.current.contentDocument;
    const win = iframeRef.current.contentWindow;
    
    // Existing click and touch events
    doc.addEventListener('click', onDocClick, true);
    doc.addEventListener('touchend', onDocTouchEnd, true);
    
    // Add scroll and touch events for interaction detection
    doc.addEventListener('scroll', handleIframeInteraction, true);
    doc.addEventListener('touchstart', handleIframeInteraction, true);
    doc.addEventListener('touchmove', handleIframeInteraction, true);
    doc.addEventListener('touchend', handleIframeInteraction, true);
    
    // Also listen on window for scroll events
    if (win) {
      win.addEventListener('scroll', handleIframeInteraction, true);
    }
    
    return () => {
      doc.removeEventListener('click', onDocClick, true);
      doc.removeEventListener('touchend', onDocTouchEnd, true);
      doc.removeEventListener('scroll', handleIframeInteraction, true);
      doc.removeEventListener('touchstart', handleIframeInteraction, true);
      doc.removeEventListener('touchmove', handleIframeInteraction, true);
      doc.removeEventListener('touchend', handleIframeInteraction, true);
      
      if (win) {
        win.removeEventListener('scroll', handleIframeInteraction, true);
      }
    };
  }, [onDocClick, onDocTouchEnd, handleIframeInteraction]);

  // Listen for iframe interaction messages
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'iframe-interaction') {
        // Pause for longer duration when interaction is inside iframe
        pauseAutoRotation(18000); // 18 seconds for iframe interactions
        
        // Visual feedback: set interacting state
        setIsInteracting(true);
        
        // Clear existing timeout
        if (interactionTimeoutRef.current) {
          clearTimeout(interactionTimeoutRef.current);
        }
        
        // Reset visual state after 4 seconds
        interactionTimeoutRef.current = setTimeout(() => {
          setIsInteracting(false);
        }, 4000);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  // Reattach listeners when template or language changes
  useEffect(() => {
    const cleanup = attachIframeListeners();
    return cleanup;
  }, [activeTemplate, i18n.language, attachIframeListeners]);

  // Cycle to next template (for rotating variant)
  const cycleToNext = () => {
    const nextIndex = (activeTemplate + 1) % templates.length;
    setActiveTemplate(nextIndex);
    pauseAutoRotation(15000);
  };

  // Rotating variant: only show if variant is 'rotating'
  const isRotatingMode = variant === 'rotating';

  return (
    <ErrorBoundary>
      <div className={`mb-16 animate-fade-in ${className}`}>
      {!isRotatingMode && (
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-newsletter-accent/10 border border-newsletter-accent/20 mb-6">
            <Play className="w-4 h-4 text-newsletter-accent" />
            <span className="text-sm font-medium text-newsletter-accent">{t('interactivePreview.badge')}</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-foreground to-newsletter-primary bg-clip-text text-transparent">
            {t('interactivePreview.title')}
          </h2>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('interactivePreview.description')}
          </p>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Mobile Template Selection - Top (only in carousel mode) */}
        {!isRotatingMode && (
        <div className="block lg:hidden mb-8">
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: "center",
              loop: true,
              containScroll: "trimSnaps"
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {templates.map((template, index) => (
                <CarouselItem key={template.id} className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2 md:basis-1/3">
                  <button
                    onClick={() => handleTemplateSelect(index)}
                    onMouseEnter={() => pauseAutoRotation()}
                    className={`
                      group relative p-4 rounded-xl border transition-all duration-300 text-left w-full h-full
                      ${activeTemplate === index
                        ? `shadow-lg` 
                        : 'border-border hover:border-primary/50 hover:bg-primary/5'
                      }
                    `}
                    style={{
                      borderColor: activeTemplate === index ? template.color : undefined,
                      backgroundColor: activeTemplate === index ? `${template.color}20` : undefined,
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className={`
                          p-2 rounded-lg transition-colors flex-shrink-0
                          ${activeTemplate === index
                            ? 'text-white'
                            : 'bg-muted text-muted-foreground group-hover:bg-primary/20'
                          }
                        `}
                        style={{
                          background: activeTemplate === index 
                            ? template.gradient
                            : undefined
                        }}
                      >
                        {template.id === 'executive' && <Briefcase size={16} />}
                        {template.id === 'investor' && <TrendingUp size={16} />}
                        {template.id === 'tech-career' && <Rocket size={16} />}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-sm text-foreground block truncate">
                          {i18n.language === 'en-US' ? (template.title_en || template.title) : template.title}
                        </span>
                        {activeTemplate === index && (
                          <div className="flex items-center gap-1 mt-1">
                            <Play size={10} style={{ color: template.color }} />
                            <span className="text-xs font-medium" style={{ color: template.color }}>{t('interactivePreview.active')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs text-foreground leading-relaxed">
                        "{i18n.language === 'en-US' ? (template.interests_en || template.interests) : template.interests}"
                      </p>
                    </div>
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex -left-12" />
            <CarouselNext className="hidden sm:flex -right-12" />
          </Carousel>

          {/* Rotation Indicator - Mobile */}
          <div className="flex justify-center gap-2 mt-4">
            {templates.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleTemplateSelect(index)}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-300
                    ${activeTemplate === index ? 'w-6' : 'bg-muted-foreground/30 hover:bg-muted-foreground/60'}
                  `}
                  style={{
                    backgroundColor: activeTemplate === index ? templates[activeTemplate]?.color : undefined
                  }}
                />
            ))}
          </div>
        </div>
        )}

        <div className="flex justify-center items-start gap-8">
          
          {/* Desktop Sidebar Template Selection (only in carousel mode) */}
          {!isRotatingMode && (
          <div className="hidden lg:flex flex-col gap-4 min-w-[300px]">
            {templates.map((template, index) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(index)}
                onMouseEnter={() => pauseAutoRotation()}
                className={`
                  group relative p-4 rounded-xl border transition-all duration-300 text-left hover:scale-[1.02]
                  ${activeTemplate === index
                    ? `shadow-lg`
                    : 'border-border hover:border-primary/50 hover:bg-primary/5'
                  }
                `}
                style={{
                  borderColor: activeTemplate === index ? template.color : undefined,
                  backgroundColor: activeTemplate === index ? `${template.color}20` : undefined,
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div 
                    className={`
                      p-3 rounded-lg transition-colors flex-shrink-0
                      ${activeTemplate === index
                        ? 'text-white'
                        : 'bg-muted text-muted-foreground group-hover:bg-primary/20'
                      }
                    `}
                    style={{
                      background: activeTemplate === index 
                        ? template.gradient
                        : undefined
                    }}
                  >
                    {template.id === 'executive' && <Briefcase size={18} />}
                    {template.id === 'investor' && <TrendingUp size={18} />}
                    {template.id === 'tech-career' && <Rocket size={18} />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">
                        {i18n.language === 'en-US' ? (template.title_en || template.title) : template.title}
                      </span>
                      {activeTemplate === index && (
                        <div className="flex items-center gap-1">
                          <Play size={12} style={{ color: template.color }} />
                          <span className="text-xs font-medium" style={{ color: template.color }}>{t('interactivePreview.active')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-foreground leading-relaxed bg-muted/30 p-3 rounded-lg border border-muted">
                    "{i18n.language === 'en-US' ? (template.interests_en || template.interests) : template.interests}"
                  </p>
                </div>
              </button>
            ))}

            {/* Desktop Rotation Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {templates.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleTemplateSelect(index)}
                    className={`
                      w-2 h-2 rounded-full transition-all duration-300
                      ${activeTemplate === index ? 'w-6' : 'bg-muted-foreground/30 hover:bg-muted-foreground/60'}
                    `}
                    style={{
                      backgroundColor: activeTemplate === index ? templates[activeTemplate]?.color : undefined
                    }}
                  />
              ))}
            </div>

          </div>
          )}

          {/* Phone Mockup with Newsletter Content */}
          <div className="flex justify-center">
            <div className="relative max-w-sm">
              {/* Phone Frame */}
              <div 
                className={`relative bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl transition-all duration-300`}
                style={isInteracting ? getGlowStyles() : {}}
              >
                <div className="bg-black rounded-[2rem] p-1">
                  <div className="bg-gray-900 rounded-[1.75rem] overflow-hidden h-[600px] relative">
                    
                    {/* Status Bar */}
                    <div className="bg-gray-800 px-4 py-2 flex justify-between items-center text-xs border-b border-gray-700">
                      <span className="font-medium text-white">{currentTime}</span>
                      <div className="flex items-center gap-1 text-white">
                        <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                        <span>📶</span>
                        <span>🔋</span>
                      </div>
                    </div>
                    
                    {/* Email Header */}
                    <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{
                            background: currentTemplate?.gradient 
                              ? currentTemplate.gradient
                              : 'linear-gradient(135deg, #6b7280, #4b5563)'
                          }}
                        >
                          <span className="text-white font-bold text-sm">N</span>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-white">NewsForYou</div>
                          <div className="text-xs text-gray-400">feed@newsforyou.ai</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3 h-3" style={{ color: currentTemplate?.color || 'hsl(var(--newsletter-primary))' }} />
                          <span className="text-xs font-medium" style={{ color: currentTemplate?.color || 'hsl(var(--newsletter-primary))' }}>07:00</span>
                        </div>
                      </div>
                    </div>

                     {/* Newsletter Content */}
                    <div 
                      className="h-full overflow-y-auto bg-gray-900 scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600"
                      onScroll={() => pauseAutoRotation(10000)}
                      onMouseEnter={() => pauseAutoRotation(8000)}
                      onTouchStart={() => pauseAutoRotation(10000)}
                      onTouchMove={() => pauseAutoRotation(10000)}
                      onTouchEnd={() => pauseAutoRotation(10000)}
                    >
                      {isLoadingFirst ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-newsletter-primary mx-auto mb-3" />
                            <p className="text-sm text-gray-400">{t('interactivePreview.loading')}</p>
                          </div>
                        </div>
                      ) : error ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <p className="text-sm text-red-400 mb-2">{t('interactivePreview.errorLoading')}</p>
                            <p className="text-xs text-gray-500">{error}</p>
                          </div>
                        </div>
                      ) : currentTemplate ? (
                        <iframe
                          ref={iframeRef}
                          title="Newsletter preview"
                          srcDoc={buildSrcDoc(i18n.language === 'en-US' && currentTemplate.html_content_en ? currentTemplate.html_content_en : currentTemplate.html_content)}
                          sandbox="allow-scripts allow-same-origin"
                          className="w-full border-0"
                          style={{ minHeight: 510, minWidth: 350 }}
                          onLoad={attachIframeListeners}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Label and CTA */}
          <div className="text-center mt-6">
            <p className="text-sm font-medium text-muted-foreground whitespace-pre-line">
              {t('interactivePreview.labels.emailDelivery')}
            </p>
          </div>

              {/* Floating elements */}
              <div 
                className="absolute -top-4 -right-4 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg animate-bounce"
                style={{ backgroundColor: currentTemplate?.color || 'hsl(var(--newsletter-primary))' }}
              >
                {t('interactivePreview.labels.newBadge')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ErrorBoundary>
  );
};